import { getLang } from '@/helpers/getLang'
import { setUserJwtToken } from '@/helpers/setRequestHeaders'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { Manager } from './admin/managersApi'

export type TAuthor = {
    id: number
    name: string
    email: string
    phone: string
    password: string
    role: string
    company_name: string
    company_post: string
    company_address: string
    expectations_of_learning: any[]
    question: string
    activated: boolean
}

export type TChatMessage = {
    id: number
    author_id: number
    chat_id: number
    text: string
    author: TAuthor
    chat: Omit<TChat, 'messages'>
}

export type TChat = {
    id: number
    webinar_id: number
    messages: TChatMessage[]
}

export type TChatMessagePayload = {
    author: number
    text: string
    chat: number
}

export type TQuestionType = 'VARIANTS' | 'MULTIPLE_VARIANTS' | 'TEXT'

export type TQuestion = {
    id: number
    title: string
    answer_options: string[]
    correct_answer: string
    correct_answers_list: any[]
    type: TQuestionType
}

export type TQuiz = {
    id: number
    title: string
    timeToAnswer: string
    webinar_id: number
    questions: TQuestion[]
    webinar: Webinar
    answers: any[]
}

export interface Webinar {
    id: number
    title: string
    chat: TChat
    description: string
    photo: string
    date: string
    link: string
    translit: string
    speaker: Manager
    time: string
    data: string
    webinar_link: string
}

export interface WebinarDto {
    title: string
    description: string
    photo: string
    date: string
    speaker: number
}

export interface WebinarUpdateDto extends WebinarDto {
    id: number
}

export type TQuestionAnswer = {
    text: string
    multi_text: string[]
    question: number
    author: number
}

export interface IAddQuizAnswerDto {
    question_answers: TQuestionAnswer[]
    quiz: number
    author: number
}

interface IAddQuizAnswerResponse {
    id: number
    correct: boolean
    quiz_id: number
    author_id: number
    creation_date: string
    question_answers: IAddQuizAnswerQuestionAnswer[]
    quiz: IAddQuizAnswerQuiz
    author: IAddQuizAnswerAuthor
}

interface IAddQuizAnswerAuthor {
    id: number
    name: string
    email: string
    phone: string
    password: string
    role: string
    company_name: string
    company_post: string
    company_address: string
    expectations_of_learning: any[]
    question: string
    activated: boolean
}

interface IAddQuizAnswerQuiz {
    id: number
    title: string
    timeToAnswer: string
    webinar_id: number
}

interface IAddQuizAnswerQuestionAnswer {
    id: number
    correct: boolean
    text: string
    quiz_answers_id: number
    question_id: number
    author_id: number
}

// TODO:
// 1. Разделить вебинары пользователя и админа (сейчас отправляется токен пользователя из localStorage)
export const webinarApi = createApi({
    reducerPath: 'webinarApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.NEXT_PUBLIC_API_ENDPOINT || 'http://megakorm.ru:3005/api',
        prepareHeaders: setUserJwtToken
    }),
    tagTypes: ['WEBINAR'],
    endpoints: builder => ({
        getAllWebinars: builder.query<any[], { user?: number } | void>({
            query: data => {
                if (data && data?.user) {
                    return { url: `/webinars?user=${data?.user}` }
                }
                return { url: `/webinars` }
            },
            providesTags: ['WEBINAR']
        }),
        getAllWebinarsEn: builder.query<Webinar[], void>({
            query: () => ({ url: `/webinars?lang=en` }),
            providesTags: ['WEBINAR']
        }),
        getWebinarTranslit: builder.query<any, string>({
            query: id => ({ url: `/webinars/translit/${id}`, method: 'GET' }),
            providesTags: ['WEBINAR']
        }),
        getChatByWebinarId: builder.query<any, number>({
            query: id => ({ url: `/webinars/getChat/${id}`, method: 'GET' }),
            providesTags: ['WEBINAR']
        }),
        getMessagesByChatId: builder.query<any, number>({
            query: id => ({ url: `/chat/messages/${id}`, method: 'GET' })
        }),
        getQuizByWebinarId: builder.query<TQuiz, number>({
            query: id => ({ url: `/webinars/getQuiz/${id}`, method: 'GET' }),
            providesTags: ['WEBINAR']
        }),
        addQuizAnswer: builder.mutation<IAddQuizAnswerResponse, IAddQuizAnswerDto>({
            query: data => {
                return { url: `/quiz/add-answer`, method: 'POST', body: data }
            },
            extraOptions: { includeToken: true }
        }),
        getQuizAccess: builder.query<any, { userId: number; quizId: number }>({
            query: ({ userId, quizId }) => ({
                url: `/quiz/access`,
                method: 'GET',
                params: { id: userId, quiz: quizId }
            }),
            providesTags: ['WEBINAR']
        }),
        singedUpWebinar: builder.mutation<{ signedUp: boolean }, { user: number; webinar: number; signup?: boolean }>({
            query: data => ({ url: '/users/singed-up-webinar', method: 'POST', body: data }),
            invalidatesTags: ['WEBINAR']
        }),
        questionToWebinar: builder.mutation<any, {webinar: number, question: string}>({
            query: data => ({ url: '/articles/webinar/add-question', method: 'PATCH', body: data }),
            invalidatesTags: ['WEBINAR']
        })
    })
})

export const {
    useGetQuizAccessQuery,
    useLazyGetQuizAccessQuery,
    useGetAllWebinarsQuery,
    useGetMessagesByChatIdQuery,
    useGetChatByWebinarIdQuery,
    useGetWebinarTranslitQuery,
    useGetAllWebinarsEnQuery,
    useGetQuizByWebinarIdQuery,
    useAddQuizAnswerMutation,
    useSingedUpWebinarMutation,
    useQuestionToWebinarMutation
} = webinarApi
